import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "wouter";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useInitialData } from "@/lib/initial-data-context";
import type { BlogPostData } from "@/lib/initial-data";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { blogPost: initialPost } = useInitialData();

  const hasInitialData = initialPost != null && initialPost.slug === slug;
  const [post, setPost] = useState<BlogPostData | null>(hasInitialData ? initialPost : null);
  const [loading, setLoading] = useState(!hasInitialData);
  const [error, setError] = useState("");
  const loadedSlug = useRef<string | undefined>(hasInitialData ? slug : undefined);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    if (!slug) return;
    if (loadedSlug.current === slug) return;
    loadedSlug.current = slug;
    setLoading(true);
    setError("");
    setPost(null);
    apiFetch(`/blog/posts/${slug}`)
      .then(setPost)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h1 className="text-3xl font-serif font-bold mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-8">This blog post doesn't exist or has been removed.</p>
          <Button asChild variant="outline">
            <Link href="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  function getImageUrl(imagePath: string) {
    return `${BASE}/api/storage${imagePath}`;
  }

  const BASE_SITE_URL = "https://www.tallahasseelassiepetcare.com";
  const postUrl = `${BASE_SITE_URL}/blog/${post.slug}`;
  const postDescription = post.excerpt || `Read "${post.title}" on Tally Lassie Times - pet care blog by Tallahassee Lassie Pet Care`;
  const postImage = post.coverImage
    ? post.coverImage.startsWith("/objects")
      ? `${BASE_SITE_URL}${BASE}/api/storage${post.coverImage}`
      : post.coverImage
    : `${BASE_SITE_URL}/opengraph.jpg`;
  const createdAt = new Date(post.createdAt);
  const updatedAt = post.updatedAt ? new Date(post.updatedAt) : createdAt;
  const wasUpdated = updatedAt.getTime() > createdAt.getTime() + 1000;

  const blogPostingJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: postDescription,
    datePublished: post.createdAt,
    ...(post.updatedAt ? { dateModified: post.updatedAt } : {}),
    image: postImage,
    url: postUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": postUrl,
    },
    author: {
      "@type": "Organization",
      name: "Tallahassee Lassie Pet Care",
      url: BASE_SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Tallahassee Lassie Pet Care",
      url: BASE_SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_SITE_URL}/opengraph.jpg`,
      },
    },
  };

  return (
    <div className="py-16 md:py-24">
      <SEO
        title={post.title}
        description={postDescription}
        path={`/blog/${post.slug}`}
        ogImage={postImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingJsonLd) }}
      />
      <div className="container mx-auto px-4 max-w-3xl">
        <Reveal>
          <Link href="/blog" className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Tally Lassie Times
          </Link>
        </Reveal>

        {post.coverImage && (
          <Reveal>
            <img
              src={post.coverImage.startsWith("/objects") ? getImageUrl(post.coverImage) : post.coverImage}
              alt={post.title}
              className="w-full max-h-[32rem] object-contain bg-muted mb-8"
              style={{ borderRadius: "16px 0 16px 0" }}
            />
          </Reveal>
        )}

        <Reveal>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
            {post.title}
          </h1>
          <p className="text-muted-foreground mb-8">
            {wasUpdated ? "Updated " : "Published "}
            {(wasUpdated ? updatedAt : createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </Reveal>

        <Reveal>
          <div
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Reveal>
      </div>
    </div>
  );
}
